import { useState, useCallback, useRef } from 'react'
import { Modal, btnPrimary, btnSecondary, btnDanger, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { ConfigVersionDiffView } from '../fleet/ConfigVersionDiffView'
import { buildVersionEntries } from './config-version-entries'

interface ActiveConfig {
  readonly id: string
  readonly configVersion: number
  readonly configJson: Record<string, unknown>
  readonly pushedFrom: string
  readonly pushedAt: string | null
  readonly minUdmVersion: string | null
  readonly maxUdmVersion: string | null
  readonly minAlphaVersion: string | null
}

interface HistoryEntry {
  readonly id: string
  readonly configVersion: number
  readonly configJson: Record<string, unknown>
  readonly pushedFrom: string
  readonly pushedAt: string | null
  readonly changeReason: string | null
  readonly changedAt: string
}

export interface InstrumentConfigEditModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly deviceId: string
  readonly deviceName: string
}

export function InstrumentConfigEditModal({ isOpen, onClose, deviceId, deviceName }: InstrumentConfigEditModalProps) {
  const [activeConfig, setActiveConfig] = useState<ActiveConfig | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [configText, setConfigText] = useState('{}')
  const [changeReason, setChangeReason] = useState('')
  const [minUdmVersion, setMinUdmVersion] = useState('')
  const [maxUdmVersion, setMaxUdmVersion] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showCompare, setShowCompare] = useState(false)
  const [compareA, setCompareA] = useState<number | null>(null)
  const [compareB, setCompareB] = useState<number | null>(null)

  // Track which deviceId we've loaded so we reload when a different device opens
  const loadedForRef = useRef<string | null>(null)

  const loadConfig = useCallback(async (id: string) => {
    setLoading(true)
    setSaveError(null)
    setShowHistory(false)
    try {
      const [configRes, histRes] = await Promise.all([
        api.get<ActiveConfig | null>(`/instruments/configs/${id}`),
        api.get<HistoryEntry[]>(`/instruments/configs/${id}/history?limit=20`),
      ])
      const cfg = configRes.data ?? null
      setActiveConfig(cfg)
      setConfigText(cfg ? JSON.stringify(cfg.configJson, null, 2) : '{}')
      setMinUdmVersion(cfg?.minUdmVersion ?? '')
      setMaxUdmVersion(cfg?.maxUdmVersion ?? '')
      setChangeReason('')
      setJsonError(null)

      const histData = histRes.data ?? []
      setHistory(histData)
      setHistoryTotal(histRes.meta?.total ?? histData.length)
      setShowCompare(false)
      setCompareA(null)
      setCompareB(null)
    } catch {
      setSaveError('Failed to load config')
    } finally {
      setLoading(false)
    }
  }, [])

  // Trigger load whenever the modal opens for a (possibly different) device.
  // This avoids useEffect while still reloading when deviceId changes.
  if (isOpen && loadedForRef.current !== deviceId) {
    loadedForRef.current = deviceId
    void loadConfig(deviceId)
  }
  if (!isOpen && loadedForRef.current !== null) {
    loadedForRef.current = null
  }

  const handleSave = useCallback(async () => {
    setJsonError(null)
    setSaveError(null)

    let parsed: Record<string, unknown>
    try {
      const result: unknown = JSON.parse(configText)
      if (typeof result !== 'object' || result === null || Array.isArray(result)) {
        setJsonError('Config must be a JSON object')
        return
      }
      parsed = result as Record<string, unknown>
    } catch {
      setJsonError('Invalid JSON — please fix the syntax before saving')
      return
    }

    setSaving(true)
    try {
      await api.post(`/instruments/configs/${deviceId}/push`, {
        configJson: parsed,
        changeReason: changeReason.trim() || undefined,
        minUdmVersion: minUdmVersion.trim() || undefined,
        maxUdmVersion: maxUdmVersion.trim() || undefined,
      })
      loadedForRef.current = null // force reload
      await loadConfig(deviceId)
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [configText, changeReason, minUdmVersion, maxUdmVersion, deviceId, loadConfig])

  const handleRollback = useCallback(
    async (targetVersion: number) => {
      setSaveError(null)
      setSaving(true)
      try {
        await api.post(`/instruments/configs/${deviceId}/rollback`, { targetVersion })
        loadedForRef.current = null // force reload
        await loadConfig(deviceId)
      } catch (err) {
        setSaveError(err instanceof ApiError ? err.message : 'Rollback failed')
      } finally {
        setSaving(false)
      }
    },
    [deviceId, loadConfig],
  )

  const sectionHeader: React.CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.textHeading,
    marginBottom: '0.5rem',
    marginTop: '1.25rem',
  }

  const metaRow: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
  }

  const metaItem: React.CSSProperties = {
    fontSize: '0.8125rem',
    color: colors.textMuted,
  }

  const metaValue: React.CSSProperties = {
    color: colors.text,
    fontWeight: 500,
  }

  const historyRow: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '0.8125rem',
  }

  const footer: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  }

  const versionEntries = buildVersionEntries(activeConfig, history)
  const canCompare = versionEntries.length >= 2
  // Defaults: B = latest, A = previous.
  const defaultB = versionEntries[0]?.version ?? null
  const defaultA = versionEntries[1]?.version ?? null
  const selectedB = compareB ?? defaultB
  const selectedA = compareA ?? defaultA
  const sideFor = (version: number | null) => versionEntries.find((e) => e.version === version) ?? null
  const leftSide = sideFor(selectedA)
  const rightSide = sideFor(selectedB)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Config: ${deviceName}`} width="720px">
      {loading ? (
        <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading config...</div>
      ) : (
        <>
          {/* Current config metadata */}
          {activeConfig && (
            <>
              <div style={sectionHeader}>Current Config</div>
              <div style={metaRow}>
                <span style={metaItem}>
                  Version: <span style={metaValue}>v{activeConfig.configVersion}</span>
                </span>
                <span style={metaItem}>
                  Source: <span style={metaValue}>{activeConfig.pushedFrom}</span>
                </span>
                {activeConfig.pushedAt && (
                  <span style={metaItem}>
                    Pushed: <span style={metaValue}>{new Date(activeConfig.pushedAt).toLocaleString()}</span>
                  </span>
                )}
                {activeConfig.minUdmVersion && (
                  <span style={metaItem}>
                    Min UDM: <span style={metaValue}>{activeConfig.minUdmVersion}</span>
                  </span>
                )}
                {activeConfig.maxUdmVersion && (
                  <span style={metaItem}>
                    Max UDM: <span style={metaValue}>{activeConfig.maxUdmVersion}</span>
                  </span>
                )}
              </div>
            </>
          )}

          {/* JSON editor */}
          <div style={sectionHeader}>Config JSON</div>
          <textarea
            value={configText}
            onChange={(e) => {
              setConfigText(e.target.value)
              setJsonError(null)
            }}
            rows={14}
            spellCheck={false}
            style={{
              ...input,
              width: '100%',
              fontFamily: 'monospace',
              fontSize: '0.8125rem',
              resize: 'vertical',
              boxSizing: 'border-box',
              ...(jsonError ? { borderColor: colors.danger } : {}),
            }}
          />
          {jsonError && (
            <div style={{ color: colors.danger, fontSize: '0.8125rem', marginTop: '0.25rem' }}>{jsonError}</div>
          )}

          {/* Compatibility fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
            <div>
              <label htmlFor="min-udm-version" style={labelStyle}>
                Min UDM Version (optional)
              </label>
              <input
                id="min-udm-version"
                style={{ ...input, width: '100%' }}
                value={minUdmVersion}
                onChange={(e) => setMinUdmVersion(e.target.value)}
                placeholder="e.g. 1.6.0"
              />
            </div>
            <div>
              <label htmlFor="max-udm-version" style={labelStyle}>
                Max UDM Version (optional)
              </label>
              <input
                id="max-udm-version"
                style={{ ...input, width: '100%' }}
                value={maxUdmVersion}
                onChange={(e) => setMaxUdmVersion(e.target.value)}
                placeholder="e.g. 2.0.0"
              />
            </div>
          </div>

          {/* Change reason */}
          <div style={{ marginTop: '0.75rem' }}>
            <label htmlFor="change-reason" style={labelStyle}>
              Change Reason (optional)
            </label>
            <input
              id="change-reason"
              style={{ ...input, width: '100%' }}
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Describe the change..."
            />
          </div>

          {saveError && (
            <div style={{ color: colors.danger, fontSize: '0.875rem', marginTop: '0.75rem' }}>{saveError}</div>
          )}

          {/* Version history */}
          <div style={{ marginTop: '1.25rem' }}>
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.primary,
                fontWeight: 600,
                fontSize: '0.875rem',
                padding: 0,
              }}
              onClick={() => setShowHistory((v) => !v)}
            >
              {showHistory ? 'Hide' : 'Show'} Version History ({historyTotal})
            </button>

            {showHistory && (
              <div style={{ marginTop: '0.75rem', maxHeight: '240px', overflowY: 'auto' }}>
                {history.length === 0 ? (
                  <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>No history yet.</div>
                ) : (
                  history.map((entry) => (
                    <div key={entry.id} style={historyRow}>
                      <div>
                        <strong style={{ color: colors.text }}>v{entry.configVersion}</strong>
                        <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>
                          {new Date(entry.changedAt).toLocaleString()}
                        </span>
                        <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>via {entry.pushedFrom}</span>
                        {entry.changeReason && (
                          <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>— {entry.changeReason}</span>
                        )}
                      </div>
                      <button
                        style={{ ...btnDanger, fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                        disabled={saving}
                        onClick={() => void handleRollback(entry.configVersion)}
                      >
                        Rollback
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Version comparison */}
          {canCompare && (
            <div style={{ marginTop: '1.25rem' }}>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: colors.primary,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  padding: 0,
                }}
                onClick={() => setShowCompare((v) => !v)}
              >
                {showCompare ? 'Hide' : 'Compare'} Versions
              </button>

              {showCompare && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label htmlFor="compare-a" style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
                      Compare
                    </label>
                    <select
                      id="compare-a"
                      value={selectedA ?? ''}
                      onChange={(e) => setCompareA(Number(e.target.value))}
                      style={{ ...input, width: 'auto' }}
                    >
                      {versionEntries.map((e) => (
                        <option key={e.version} value={e.version}>
                          v{e.version}
                          {e.meta?.changedAt ? ` — ${new Date(e.meta.changedAt).toLocaleDateString()}` : ''}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>with</span>
                    <select
                      id="compare-b"
                      aria-label="Compare with"
                      value={selectedB ?? ''}
                      onChange={(e) => setCompareB(Number(e.target.value))}
                      style={{ ...input, width: 'auto' }}
                    >
                      {versionEntries.map((e) => (
                        <option key={e.version} value={e.version}>
                          v{e.version}
                          {e.meta?.changedAt ? ` — ${new Date(e.meta.changedAt).toLocaleDateString()}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {leftSide && rightSide && <ConfigVersionDiffView left={leftSide} right={rightSide} />}
                </div>
              )}
            </div>
          )}

          <div style={footer}>
            <button style={btnSecondary} onClick={onClose} disabled={saving}>
              Close
            </button>
            <button style={btnPrimary} onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving...' : 'Save Config'}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}
